import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Grid,
  Tooltip,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useActivationMetrics } from '../hooks/useActivation';

interface ActivationMetricsWidgetProps {
  siteId: number;
  planTier?: string;
}

const isStarterPlan = (planTier?: string): boolean => {
  const normalized = (planTier || '').trim().toUpperCase();
  return normalized === 'STARTER' || normalized === 'FREE';
};

// Circular Progress Component with visible legend
const CircularProgressWithLegend: React.FC<{
  criteria: { label: string; met: boolean; description: string; }[];
  score: number;
  title: string;
}> = ({ criteria, score, title }) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  
  const containerSize = 300;
  const circleSize = 150;
  const strokeWidth = 18;
  const radius = (circleSize - strokeWidth) / 2;
  const segmentGap = 15;
  const totalGaps = criteria.length * segmentGap;
  const segmentAngle = (360 - totalGaps) / criteria.length;

  const getProgressStatus = (criterion: { label: string; met: boolean; description: string }) => {
    if (criterion.met) return 'completed';
    const progressMatch = criterion.description.match(/(\d+)\/(\d+)/);
    if (progressMatch) {
      const current = parseInt(progressMatch[1]);
      const total = parseInt(progressMatch[2]);
      if (current > 0 && current < total) return 'in-progress';
    }
    return 'not-started';
  };

  const getColor = (criterion: { label: string; met: boolean; description: string }) => {
    const status = getProgressStatus(criterion);
    switch (status) {
      case 'completed': return '#10B981';
      case 'in-progress': return '#F59E0B';
      case 'not-started': return '#D1D5DB';
      default: return '#D1D5DB';
    }
  };

  return (
    <Box>
      {/* Circular Progress */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box sx={{ position: 'relative', width: { xs: 180, sm: 200, md: 220 }, height: { xs: 180, sm: 200, md: 220 } }}>
          <svg 
            width="100%" 
            height="100%" 
            viewBox={`0 0 ${containerSize} ${containerSize}`}
            style={{ display: 'block' }}
          >
            {criteria.map((criterion, index) => {
              const startAngle = -90 + index * (segmentAngle + segmentGap);
              const endAngle = startAngle + segmentAngle;
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              const x1 = containerSize / 2 + radius * Math.cos(startRad);
              const y1 = containerSize / 2 + radius * Math.sin(startRad);
              const x2 = containerSize / 2 + radius * Math.cos(endRad);
              const y2 = containerSize / 2 + radius * Math.sin(endRad);
              const largeArcFlag = segmentAngle > 180 ? 1 : 0;
              const pathData = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;

              return (
                <Tooltip
                  key={index}
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 0.5 }}>
                        {criterion.label}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        {criterion.description}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                  enterDelay={200}
                  enterTouchDelay={0}
                  leaveTouchDelay={3000}
                >
                  <g>
                    {/* Main segment */}
                    <path
                      d={pathData}
                      fill="none"
                      stroke={getColor(criterion)}
                      strokeWidth={strokeWidth}
                      strokeLinecap="round"
                      style={{
                        opacity: hoveredIndex === null || hoveredIndex === index ? 1 : 0.4,
                        transition: 'opacity 0.2s ease',
                        cursor: 'pointer'
                      }}
                    />
                    {/* Invisible wider path for better hover area */}
                    <path
                      d={pathData}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={strokeWidth + 20}
                      strokeLinecap="round"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    />
                  </g>
                </Tooltip>
              );
            })}
          </svg>
          
          {/* Center Score */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center'
            }}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                color: 'text.primary',
                lineHeight: 1 
              }}
            >
              {score}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              /100
            </Typography>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                color: score >= 75 ? '#10B981' : score >= 50 ? '#F59E0B' : '#3B82F6',
                fontWeight: 600,
                mt: 0.5,
                fontSize: '0.65rem'
              }}
            >
              {score >= 75 ? 'Excellent' : score >= 50 ? 'Good' : 'Getting Started'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ActivationMetricsWidget: React.FC<ActivationMetricsWidgetProps> = ({ siteId, planTier }) => {
  const { data: metrics, isLoading } = useActivationMetrics(siteId);
  const [open, setOpen] = React.useState(false);
  const starterPlan = isStarterPlan(planTier);

  if (isLoading || !metrics) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Activation Progress
          </Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  const { day7, day30, siteAgeDays, engagementSignals } = metrics;

  // Day 7 Criteria
  const day7Criteria = [
    {
      label: 'Connect your site',
      met: day7.siteConnected,
      description: 'Add your website and verify ownership to start tracking'
    },
    {
      label: starterPlan ? 'Explore your dashboard' : 'View dashboard 2+ times',
      met: day7.dashboardViews >= day7.dashboardViewsRequired,
      description: `${day7.dashboardViews}/${day7.dashboardViewsRequired} ${day7.dashboardViewsRequired === 1 ? 'view' : 'sessions'} completed`
    },
    {
      label: 'Explore readiness pillar',
      met: day7.pillarsViewed >= day7.pillarsViewedRequired,
      description: `Viewed ${day7.pillarsViewed}/${day7.pillarsViewedRequired} pillar analysis`
    },
    {
      label: 'Complete an action',
      met: day7.actionsCompleted >= day7.actionsCompletedRequired,
      description: `${day7.actionsCompleted}/${day7.actionsCompletedRequired} recommendations implemented`
    },
    {
      label: 'Visit SEO Timeline',
      met: day7.timelineViewed,
      description: day7.timelineViewed
        ? 'You\'ve explored your SEO journey timeline'
        : 'See your site\'s progress over time → go to Timeline'
    },
  ].filter((criterion) => !starterPlan || criterion.label !== 'Visit SEO Timeline');

  // Engagement signals with human-readable descriptions
  const signalItems = engagementSignals ? [
    {
      icon: '\uD83D\uDCC5',
      label: 'SEO Timeline explored',
      detail: 'You visited the timeline showing your week-by-week SEO progress',
      achieved: engagementSignals.timelineViewed,
    },
    {
      icon: '\uD83D\uDCC4',
      label: 'Page analyses opened',
      detail: `You\'ve opened ${engagementSignals.pageReadinessViews} individual page readiness report${engagementSignals.pageReadinessViews !== 1 ? 's' : ''} — great for spotting page-level issues`,
      achieved: engagementSignals.pageReadinessViews > 0,
      count: engagementSignals.pageReadinessViews,
    },
    {
      icon: '\uD83D\uDCD6',
      label: 'Learning resources clicked',
      detail: `You\'ve clicked ${engagementSignals.educationLinksClicked} educational resource link${engagementSignals.educationLinksClicked !== 1 ? 's' : ''} — keep learning how SEO works`,
      achieved: engagementSignals.educationLinksClicked > 0,
      count: engagementSignals.educationLinksClicked,
    },
    {
      icon: '\uD83D\uDD04',
      label: 'Same fix applied on multiple pages',
      detail: 'You applied the same type of SEO fix across 2 or more pages — building a consistent pattern',
      achieved: engagementSignals.pageFixesRepeated,
    },
    {
      icon: '\uD83D\uDD17',
      label: 'Internal linking improved',
      detail: 'You completed an internal-linking action — helping search engines discover more of your content',
      achieved: engagementSignals.internalLinksAdded,
    },
    {
      icon: '\uD83D\uDD0D',
      label: 'Indexing score improved',
      detail: 'Your indexability score went up compared to yesterday — more pages are being found by search engines',
      achieved: engagementSignals.indexingImproved,
    },
    {
      icon: '\uD83C\uDF93',
      label: 'Ready to graduate to Performance mode',
      detail: 'Your site has unlocked all the early-stage milestones and is ready for the full Performance dashboard',
      achieved: engagementSignals.modeTransitionUnlocked,
    },
  ] : [];

  // Day 30 Criteria
  const day30Criteria = [
    {
      label: 'Active 3+ weeks',
      met: day30.activeWeeks >= day30.activeWeeksRequired,
      description: `${day30.activeWeeks}/${day30.activeWeeksRequired} weeks with activity`
    },
    {
      label: 'Complete 5+ actions',
      met: day30.actionsCompleted >= day30.actionsCompletedRequired,
      description: `${day30.actionsCompleted}/${day30.actionsCompletedRequired} actions completed`
    },
    {
      label: 'Touch 3+ pillars',
      met: day30.pillarsTouched >= day30.pillarsTouchedRequired,
      description: `${day30.pillarsTouched}/${day30.pillarsTouchedRequired} pillars explored`
    },
    {
      label: 'Improve a pillar',
      met: day30.pillarsImproved >= day30.pillarsImprovedRequired,
      description: `${day30.pillarsImproved}/${day30.pillarsImprovedRequired} pillar improved`
    },
  ];

  return (
    <Card 
      sx={{ 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        width: '100%'
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box flex={1}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: { xs: '1rem', sm: '1.125rem' },
                fontWeight: 700,
                color: 'text.primary',
                mb: 0.5
              }}
            >
              Activation Progress
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}
            >
              Day 7: <strong>{day7.score}/100</strong> • Day 30: <strong>{day30.score}/100</strong> • Site age: {siteAgeDays} days
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setOpen(true)}
            sx={{ ml: 2, flexShrink: 0 }}
          >
            View Details
          </Button>
        </Box>
      </CardContent>

      {/* Modal Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Activation Milestones
          </Typography>
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Complete these milestones in your first 30 days. Site age: <strong>{siteAgeDays} days</strong>. Hover over segments for details.
          </Typography>
          
          <Grid container spacing={3}>
            {/* Day 7 */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: '#FAFAFA',
                  borderRadius: 2,
                  p: 2.5,
                  border: '1px solid',
                  borderColor: '#E5E7EB',
                  textAlign: 'center'
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    mb: 2,
                    color: 'text.primary'
                  }}
                >
                  🚀 Day 7 Goals
                </Typography>
                <CircularProgressWithLegend
                  criteria={day7Criteria}
                  score={day7.score}
                  title="Day 7"
                />
              </Box>
            </Grid>

            {/* Day 30 */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: '#FAFAFA',
                  borderRadius: 2,
                  p: 2.5,
                  border: '1px solid',
                  borderColor: '#E5E7EB',
                  textAlign: 'center'
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    mb: 2,
                    color: 'text.primary'
                  }}
                >
                  🎯 Day 30 Goals
                </Typography>
                <CircularProgressWithLegend
                  criteria={day30Criteria}
                  score={day30.score}
                  title="Day 30"
                />
              </Box>
            </Grid>
          </Grid>

          {/* Engagement Signals */}
          {signalItems.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Your Exploration Progress
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These show how deeply you are using the platform. They do not affect your activation score — they are here to celebrate your progress.
              </Typography>
              <Grid container spacing={1.5}>
                {signalItems.map((signal, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: signal.achieved ? 'success.light' : 'divider',
                        bgcolor: signal.achieved ? '#F0FDF4' : '#FAFAFA',
                        opacity: signal.achieved ? 1 : 0.6,
                      }}
                    >
                      <Typography sx={{ fontSize: '1.25rem', lineHeight: 1 }}>{signal.icon}</Typography>
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: signal.achieved ? 'success.dark' : 'text.secondary' }}>
                            {signal.label}
                          </Typography>
                          {'count' in signal && signal.count !== undefined && signal.count > 0 && (
                            <Chip label={signal.count} size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {signal.detail}
                        </Typography>
                      </Box>
                      {signal.achieved
                        ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, flexShrink: 0 }} />
                        : <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', fontSize: 18, flexShrink: 0 }} />}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Achievement Banner */}
      {(day7.score >= 100 || day30.score >= 100) && (
        <CardContent sx={{ pt: 0 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: '#F0FDF4',
              borderRadius: 2,
              border: '2px solid #10B981',
              textAlign: 'center'
            }}
          >
            <Typography variant="body1" sx={{ color: '#059669', fontWeight: 700, mb: 0.5 }}>
              {day7.score >= 100 && day30.score >= 100 
                ? '🎉 Full Activation Achieved!'
                : day30.score >= 100
                ? '🎉 Day-30 Milestone Reached!'
                : '🎉 Day-7 Milestone Reached!'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#047857', fontSize: '0.875rem' }}>
              You're making excellent progress!
            </Typography>
          </Box>
        </CardContent>
      )}
    </Card>
  );
};

export default ActivationMetricsWidget;
